// Distribution.js
import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './Distribution.css';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const INDEX_OPTIONS = [
  { label: 'NIFTY', value: 'NIFTY' },
  { label: 'BANKNIFTY', value: 'BANKNIFTY' },
  { label: 'FINNIFTY', value: 'FINNIFTY' },
  { label: 'MIDCPNIFTY', value: 'MIDCPNIFTY' },
];

const Distribution = React.memo(({ index = 'NIFTY' }) => {
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [pcrValue, setPcrValue] = useState(null);

  const fetchData = useCallback(async (idx) => {
    setError('');
    try {
      const url = `${API_BASE_URL}/api/pcr?index=${encodeURIComponent(idx)}`;
      const res = await fetch(url);
      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || 'Request failed');
      }

      const toNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        if (typeof value === 'string') {
          const num = parseFloat(value.replace(/,/g, ''));
          return Number.isFinite(num) ? num : 0;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const payload = json.data || {};
      const totalCallOi = toNumber(json.total_call_oi_lots ?? payload.total_call_oi_lots ?? payload.total_call_oi);
      const totalPutOi = toNumber(json.total_put_oi_lots ?? payload.total_put_oi_lots ?? payload.total_put_oi);

      const total = totalCallOi + totalPutOi || 1;
      const callPct = +((totalCallOi / total) * 100).toFixed(1);
      const putPct = +((totalPutOi / total) * 100).toFixed(1);

      setChartData({
        labels: ['Calls %', 'Puts %'],
        datasets: [
          {
            label: `OI Distribution (${idx})`,
            data: [callPct, putPct],
            backgroundColor: ['#6366f1', '#10b981'],
            borderWidth: 2,
          },
        ],
      });

      const pcr = json.pcr_oi_change ?? payload.pcr_oi_change ?? payload.pcr_lots ?? payload.pcr;
      setPcrValue(pcr);
    } catch (e) {
      setChartData(null);
      setPcrValue(null);
      setError(e.message || 'Error fetching data');
    }
  }, []);

  useEffect(() => {
    fetchData(index);
  }, [index, fetchData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const value = Number(ctx.parsed) || 0;
            return `${label}: ${value.toFixed(1)}%`;
          },
        },
      },
    },
  };

  return (
    <div className="distribution-container">
      <div className="search-bar">
        <select
          value={index}
          onChange={() => {}}
          className="index-select"
          disabled
        >
          {INDEX_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <h2 className="chart-title">Distribution</h2>

      <div className="distribution-chart-area">
        {error && <p className="error-text">{error}</p>}
        {chartData ? (
          <Doughnut data={chartData} options={options} />
        ) : (
          !error && <p>Loading...</p>
        )}
      </div>
      <div className="pcr-below">
        PCR: {pcrValue ?? '—'}
      </div>
    </div>
  );
});

export default Distribution;
